import type { Day } from "date-fns";

export type DatetimePickerMode = "date" | "time" | "datetime" | "dateRange" | "datetimeRange" | "timeRange";

export type DatetimePickerAppearance = "border" | "no-border" | "bottom-border";

/**
 * Public props of SCADatetimePicker.
 *
 */
export interface DatetimePickerProps {
  mode?: DatetimePickerMode;
  inline?: boolean;
  readonly?: boolean;
  appearance?: DatetimePickerAppearance;
  config?: CalendarDisplayConfig;
  dateFormat?: string;
  timeFormat?: string;
  datePrecision?: string;
  timePrecision?: string;
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  htmlFor?: string;
  alert?: boolean;
  manualInput?: boolean;
}

/**
 * Static calendar configuration
 */
export interface CalendarDisplayConfig {
  /** Upper bound for the "Months" setting in the gear popover. If > 1, the gear is shown. */
  maxMonths?: number;
  /** Show the week-number column. */
  showWeekNumbers?: boolean;
  /** First day of the week (0 = Sun). Also drives ISO week numbering. */
  weekStartsOn?: Day;
  /** Selectable date bounds (inclusive, day granularity). Also bound the year picker range. */
  min?: Date | null;
  max?: Date | null;
  /** Predicate to disable individual dates. */
  disabledDate?: (date: Date) => boolean;
  /** Minute granularity for the combined hh:mm dropdowns (range modes). */
  minuteStep?: number;
}

export const CALENDAR_DISPLAY_CONFIG_DEFAULTS = {
  maxMonths: 12,
  showWeekNumbers: true,
  weekStartsOn: 1,
  min: null,
  max: null,
  disabledDate: undefined,
  minuteStep: 15,
} as const satisfies CalendarDisplayConfig;

/** Value for single-date modes (`date`, `datetime`). */
export type SingleValue = Date | null;

/** Value for range modes (`dateRange`, `datetimeRange`, `timeRange`). */
export type RangeValue = [Date | null, Date | null];

export type DatetimePickerValue = SingleValue | RangeValue;

export const RANGE_MODES: readonly DatetimePickerMode[] = ["dateRange", "datetimeRange", "timeRange"];
export const TIME_MODES: readonly DatetimePickerMode[] = ["time", "datetime", "datetimeRange", "timeRange"];
export const DATE_MODES: readonly DatetimePickerMode[] = ["date", "datetime", "dateRange", "datetimeRange"];

export function isRangeMode(mode: DatetimePickerMode): boolean {
  return RANGE_MODES.includes(mode);
}

export function hasTime(mode: DatetimePickerMode): boolean {
  return TIME_MODES.includes(mode);
}

export function hasDate(mode: DatetimePickerMode): boolean {
  return DATE_MODES.includes(mode);
}

/** A time-of-day broken into editable parts. `null` means "not yet set". */
export interface TimeParts {
  hours: number;
  minutes: number;
  seconds: number;
}

/** A single rendered cell in a month grid. */
export interface DayCell {
  date: Date;
  day: number;
  /** Whether the date belongs to the month currently being rendered. */
  inCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  disabled: boolean;
}

/** One rendered week row: an optional ISO week number plus 7 day cells. */
export interface WeekRow {
  weekNumber: number;
  days: DayCell[];
}

/** Visual selection state of a day cell, used purely for styling. */
export interface DaySelectionState {
  selected: boolean;
  rangeStart: boolean;
  rangeEnd: boolean;
  inRange: boolean;
  /** Hover-preview portion of a not-yet-completed range. */
  preview: boolean;
  previewStart: boolean;
  previewEnd: boolean;
  /** Sweep direction of the hover preview — which side the connecting bar grows from / retracts toward. */
  previewDirection?: "forward" | "backward";
  /**  Highlight-pulse nonce for today's cell */
  flash?: number;
}
